import * as z from 'zod'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { zodResolver } from '@hookform/resolvers/zod'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import isURL from 'validator/lib/isURL'

import { deepMerge, isEmpty, noop } from '@livechat/data-utils'

import { usePreviewController } from '../../WidgetPreview/previewController'
import ImageUpload from '../../fields/ImageUpload'
import AvatarGallery from 'components/AvatarGallery'
import Button from 'components/Button'
import EnhancementButtonLoader from 'components/EnhancementButtonLoader'
import FeatureCard from 'components/FeatureCard'
import TextSwitch from 'components/TextSwitch'
import UseAsyncFn from 'components/UseAsyncFn'
import Checkbox from 'components/fields/Checkbox'
import FieldError from 'components/fields/FieldError'
import Fieldset from 'components/fields/Fieldset'
import Form from 'components/fields/Form'
import FormRow from 'components/fields/FormRow'
import Input from 'components/fields/Input'
import Legend from 'components/fields/Legend'
import Radio from 'components/fields/Radio'
import Textarea from 'components/fields/Textarea'
import { BrowserIcon, ChatIcon, EnvelopeIcon, PaperClipIcon, SparkleIcon, StarIcon } from 'components/icons'
import HelpDeskEnabledInputInset from 'components/integrations/helpdesk/HelpDeskEnabledInputInset'
import LiveChatIntegrationCard from 'components/integrations/livechat/LiveChatIntegrationCard'
import { useActiveChatIntegration } from 'hooks/useActiveChatIntegration'
import { useFeature } from 'hooks/useFeature'
import { useIntegration } from 'hooks/useIntegration'
import { useIsOnboarding } from 'hooks/useIsOnboarding'
import { useToast } from 'hooks/useToast'
import { useUser } from 'hooks/useUser'
import { enhanceContactCardMessage, updateFeature, updateOrganization } from 'lib/api'
import { AVATAR_URLS, DEFAULT_AVATAR_URL } from 'lib/constants'
import { useAppStore } from 'lib/store'
import trimWordsEnd from 'lib/trimWordsEnd'
import type { Feature, FormName } from 'types/feature'
import type { User } from 'types/user'

const AdditionalFormsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spaces.space5};
	padding-top: ${({ theme }) => theme.spaces.space3};
	margin-bottom: ${({ theme }) => theme.spaces.space7};
`

const AvatarFieldset = styled(Fieldset)<{ selectedAvatarMode: AvatarType }>`
	min-height: ${({ theme, selectedAvatarMode }) => {
		if (['link', 'upload'].includes(selectedAvatarMode)) {
			return theme.isMobile ? '90px' : '105px'
		}
		return theme.isMobile ? '120px' : '180px'
	}};
`

const AvatarFieldsetInner = styled.div`
	width: 100%;
	max-width: 380px;
	position: absolute;
	padding: 0 ${({ theme }) => theme.spaces.space2};
`

const PickingAvatarSourceRow = styled(FormRow)`
	margin-left: -${({ theme }) => theme.spaces.space2};
	margin-right: -${({ theme }) => theme.spaces.space2};
`

const SectionHeading = styled.p`
	${({ theme }) => theme.typography.captionMedium};
	color: ${({ theme }) => theme.colors.light.alternativeTextColor};
	margin: 0;
`

const FormSchema = z.object({
	email: z
		.string()
		.min(1, "Field can't be empty")
		.email()
		.regex(/.*\S.*/, "Field can't be empty"),
	welcomeText: z
		.string()
		.min(1, "Field can't be empty")
		.max(100, 'Cannot be longer than 100 characters')
		.regex(/.*\S.*/, "Field can't be empty"),
	responseTime: z.union([z.literal('minutes'), z.literal('hours'), z.literal('days'), z.null()]),
	agent: z.object({
		name: z
			.string()
			.min(1, "Field can't be empty")
			.max(50, 'Cannot be longer than 50 characters')
			.regex(/.*\S.*/, "Field can't be empty"),
		avatarUrl: z.union([z.literal(''), z.string().refine(isURL, 'Invalid URL format')]),
	}),
})

type FormValues = z.infer<typeof FormSchema>

function getFormValues(feature: Feature<'forms'>, user: User): FormValues {
	return {
		welcomeText: feature.properties.message,
		responseTime: feature.properties.responseTime,
		email: feature.properties.email ?? user.email,
		agent: {
			name: feature.properties.agent.name ?? user.name,
			avatarUrl: feature.properties.agent.avatarUrl ?? user.avatarUrl ?? DEFAULT_AVATAR_URL,
		},
	}
}

function withDefaulValues(FormComponent: typeof SupportForm) {
	const WithDefaultValues = React.forwardRef<HTMLFormElement, Props>((props, ref) => {
		const user = useUser()
		const { feature: forms } = useFeature('forms')

		if (!forms) {
			return null
		}

		return <FormComponent {...props} formRef={ref} defaultValues={getFormValues(forms, user)} />
	})
	WithDefaultValues.displayName = `WithDefaulValues(${FormComponent.displayName})`
	return WithDefaultValues
}

type Props = {
	id: string
	onSubmit?: VoidFunction
	onHasErrorsChanged?(hasErrors: boolean, id: string): void
	onIsDirtyChanged?(isDirty: boolean, id: string): void
	onIsSubmittingChanged?(isSubmitting: boolean, id: string): void
}

type AvatarType = 'gallery' | 'upload'

const getAvatarTypeFromValue = (avatarUrl: string): AvatarType => {
	if (!avatarUrl || AVATAR_URLS.includes(avatarUrl)) {
		return 'gallery'
	}

	return 'upload'
}

const SupportForm: React.FC<Props & { defaultValues: FormValues; formRef: React.ForwardedRef<HTMLFormElement> }> = ({
	id,
	formRef,
	defaultValues,
	onHasErrorsChanged,
	onIsDirtyChanged,
	onIsSubmittingChanged,
	onSubmit,
}) => {
	const store = useAppStore()
	const user = useUser()
	const showToast = useToast()
	const previewController = usePreviewController()
	const { feature: forms, mutateFeature: mutateForms } = useFeature('forms')
	const [selectedAvatarMode, setSelectedAvatarMode] = React.useState<AvatarType>(
		getAvatarTypeFromValue(defaultValues.agent.avatarUrl),
	)
	const form = useForm<FormValues>({
		defaultValues,
		mode: 'onTouched',
		resolver: zodResolver(FormSchema),
	})
	const { watch, register, reset, getValues, setValue, resetField, handleSubmit, formState } = form
	const { errors, isValid, isDirty, isSubmitting, dirtyFields } = formState
	const isResponseTimeEnabled = !!watch('responseTime')
	const isOnboarding = useIsOnboarding()
	const hasErrors = !isEmpty(errors)
	const { integration: helpDeskIntegration } = useIntegration('helpdesk')
	const { integration: liveChatIntegration } = useIntegration('livechat')
	const activeChatIntegration = useActiveChatIntegration()
	const isIntegratedWithLiveChat = Boolean(
		liveChatIntegration && liveChatIntegration.connected && liveChatIntegration.isLicenseActive,
	)

	React.useEffect(() => {
		function syncStore() {
			if (!forms) {
				return
			}

			const formValues = getValues()
			store.dispatch({
				type: 'forms/set',
				payload: deepMerge({}, forms, {
					properties: {
						email: formValues.email,
						message: formValues.welcomeText,
						responseTime: formValues.responseTime,
						agent: {
							name: formValues.agent.name,
							avatarUrl: formValues.agent.avatarUrl,
						},
					},
				}),
			})
		}

		syncStore()

		const { unsubscribe } = watch(syncStore)
		return () => unsubscribe()
	}, [getValues, store, watch, forms])

	React.useEffect(() => {
		onHasErrorsChanged?.(hasErrors, id)
	}, [id, onHasErrorsChanged, hasErrors])

	React.useEffect(() => {
		onIsSubmittingChanged?.(isSubmitting, id)
	}, [id, isSubmitting, onIsSubmittingChanged])

	React.useEffect(() => {
		onIsDirtyChanged?.(isDirty, id)
	}, [id, isDirty, onIsDirtyChanged])

	const handleReset = (event: React.FormEvent) => {
		event.preventDefault()
		reset(defaultValues)
	}

	const _onSubmit = async (formValues: FormValues) => {
		if (!isValid) {
			return
		}

		try {
			const [feature] = await Promise.all([
				updateFeature('forms', {
					...(!isEmpty(dirtyFields) && {
						properties: {
							...(dirtyFields.email && { email: formValues.email }),
							...(dirtyFields.welcomeText && { message: formValues.welcomeText }),
							...(dirtyFields.responseTime && { responseTime: formValues.responseTime }),
							...(dirtyFields.agent && {
								agent: {
									...(dirtyFields.agent.name && { name: formValues.agent.name }),
									...(dirtyFields.agent.avatarUrl && { avatarUrl: formValues.agent.avatarUrl }),
								},
							}),
						},
					}),
				}),
				isOnboarding && updateOrganization({ onboardingProgress: 'offer-support' }),
			])

			if (feature) {
				mutateForms(feature, { revalidate: false })
				reset(getFormValues(feature, user))
			}
			onSubmit?.()
			if (!isOnboarding) {
				showToast('success')
			}
		} catch {
			showToast('error')
		}
	}

	const handleEnhanceAnswer = async () => {
		const { result } = await enhanceContactCardMessage(getValues('welcomeText'))
		setValue('welcomeText', trimWordsEnd(result, 100), { shouldTouch: true, shouldDirty: true })
	}

	const handleToggleForm = async (formName: FormName, isEnabled: boolean) => {
		const feature = await updateFeature('forms', { properties: { templates: { [formName]: isEnabled } } })
		mutateForms(feature, { revalidate: false })
	}

	return (
		<>
			{!isOnboarding && (
				<AdditionalFormsWrapper>
					<SectionHeading>Let customers</SectionHeading>
					{isIntegratedWithLiveChat && <LiveChatIntegrationCard />}
					{activeChatIntegration && (
						<UseAsyncFn key="toggle-contact-form" asyncFn={(isEnabled) => handleToggleForm('contact', isEnabled)}>
							{({ callAsyncFn, loading }) => (
								<FeatureCard
									key="contact"
									title="Send email messages"
									ariaName="contact form"
									trackingName="Contact form"
									disabled={loading}
									onClick={() => previewController.showView('Contact')}
									onChange={callAsyncFn}
									isEnabled={Boolean(forms?.properties.templates.contact)}
									dataTestId="additional-form-contact"
									features={[{ icon: <EnvelopeIcon />, label: 'Simple email form' }]}
								/>
							)}
						</UseAsyncFn>
					)}
					<UseAsyncFn key="toggle-bugreport-form" asyncFn={(isEnabled) => handleToggleForm('bugreport', isEnabled)}>
						{({ callAsyncFn, loading }) => (
							<FeatureCard
								key="bugreport"
								title="Report bugs"
								ariaName="bug report form"
								trackingName="Bug report form"
								disabled={loading}
								onClick={() => previewController.showView('Contact')}
								onChange={callAsyncFn}
								isEnabled={Boolean(forms?.properties.templates.bugreport)}
								dataTestId="additional-form-bugreport"
								features={[
									{ icon: <PaperClipIcon />, label: 'Up to 3 attachments' },
									{ icon: <BrowserIcon />, label: 'Automatic system and browser detection' },
								]}
							/>
						)}
					</UseAsyncFn>
					<UseAsyncFn key="toggle-feedback-form" asyncFn={(isEnabled) => handleToggleForm('feedback', isEnabled)}>
						{({ callAsyncFn, loading }) => (
							<FeatureCard
								key="feedback"
								title="Share feedback"
								ariaName="feedback form"
								trackingName="Feedback form"
								isEnabled={Boolean(forms?.properties.templates.feedback)}
								onChange={callAsyncFn}
								onClick={() => previewController.showView('Contact')}
								disabled={loading}
								dataTestId="additional-form-feedback"
								features={[
									{ icon: <StarIcon />, label: 'Rating on a 1-5 scale' },
									{ icon: <ChatIcon />, label: 'Optional comment on the rate' },
								]}
							/>
						)}
					</UseAsyncFn>
				</AdditionalFormsWrapper>
			)}
			<FormProvider {...form}>
				<Form
					id={id}
					ref={formRef}
					onSubmit={handleSubmit(_onSubmit)}
					onReset={handleReset}
					onChange={() => previewController.showView('Homescreen', 'contact-card')}
				>
					<Input
						required
						type="email"
						label="Forward form inquires to"
						placeholder="your@email.com"
						error={errors.email?.message}
						{...(helpDeskIntegration?.connected && {
							innerInset: <HelpDeskEnabledInputInset />,
						})}
						{...register('email')}
					/>
					{!isIntegratedWithLiveChat && (
						<>
							<AvatarFieldset selectedAvatarMode={selectedAvatarMode}>
								<AvatarFieldsetInner>
									<PickingAvatarSourceRow>
										<Legend>Pick avatar</Legend>
										<TextSwitch
											options={['Gallery', 'Image upload']}
											selectedIndex={selectedAvatarMode === 'gallery' ? 0 : 1}
											onChange={(index) => setSelectedAvatarMode(index === 0 ? 'gallery' : 'upload')}
										/>
									</PickingAvatarSourceRow>
									{selectedAvatarMode === 'gallery' && (
										<AvatarGallery
											avatarURLs={AVATAR_URLS}
											watchValue={() => watch('agent.avatarUrl')}
											onChange={(index) => {
												previewController.showView('Homescreen', 'contact-card')
												resetField('agent.avatarUrl')
												setValue('agent.avatarUrl', AVATAR_URLS[index], { shouldDirty: true })
											}}
											aria-label="avatar gallery"
										/>
									)}
									{selectedAvatarMode === 'upload' && (
										<ImageUpload
											kind="form_agent_avatar"
											fieldName="agent.avatarUrl"
											label=""
											alt="Avatar"
											emptyFieldValue=""
										/>
									)}
								</AvatarFieldsetInner>
							</AvatarFieldset>
							<Input
								required
								label="Add your name"
								placeholder="Type your name"
								error={errors.agent?.name?.message}
								{...register('agent.name')}
							/>
							<UseAsyncFn asyncFn={() => handleEnhanceAnswer().catch(noop)}>
								{({ loading, callAsyncFn }) => (
									<Textarea
										required
										disabled={loading}
										label="Offer help"
										placeholder="Encourage customers to leave you a message"
										error={errors.welcomeText?.message}
										{...register('welcomeText')}
										rightAdornment={
											<EnhancementButtonLoader isVisible={loading}>
												<Button
													compact
													kind="secondary"
													type="button"
													loading={loading}
													onClick={callAsyncFn}
													disabled={loading}
													aria-label="enhance welcome message with AI"
												>
													<SparkleIcon />
												</Button>
											</EnhancementButtonLoader>
										}
									/>
								)}
							</UseAsyncFn>
							<Fieldset>
								<Checkbox
									label="Response time"
									checked={isResponseTimeEnabled}
									onChange={(event) =>
										setValue('responseTime', event.target.checked ? 'minutes' : null, { shouldDirty: true })
									}
								/>
								{isResponseTimeEnabled && (
									<Fieldset>
										<Legend css={css({ fontWeight: 400 })}>We typically respond within a few...</Legend>
										<Radio label="minutes" value="minutes" {...register('responseTime')} />
										<Radio label="hours" value="hours" {...register('responseTime')} />
										<Radio label="days" value="days" {...register('responseTime')} />
										{errors.responseTime?.message && <FieldError>{errors.responseTime?.message}</FieldError>}
									</Fieldset>
								)}
							</Fieldset>
						</>
					)}
				</Form>
			</FormProvider>
		</>
	)
}

SupportForm.displayName = 'SupportForm'

export default withDefaulValues(SupportForm)
